import React, { useState, useEffect, useCallback } from 'react';
import { reportAPI } from '../../services/api';
import { useBranchStore } from '../../store';
import { toast } from 'react-toastify';
import { exportTableToExcel } from '../../utils/excel';
import DateTimeRange, { rangeParams } from '../../components/DateTimeRange/DateTimeRange';
import './Reports.css';

const ReportCard = ({ icon, title, onExport, children }) => (
    <div className="reports-card">
        <div className="reports-card-header">
            <h2><i className={`fas ${icon}`}></i> {title}</h2>
            {onExport && <button className="btn-secondary-modern" onClick={onExport}><i className="fas fa-file-excel"></i> Export Excel</button>}
        </div>
        {children}
    </div>
);

const Reports = () => {
    const { selectedBranch } = useBranchStore();
    const [loading, setLoading] = useState(true);
    const [usage, setUsage] = useState([]);
    const [revenue, setRevenue] = useState({ byDay: [], total: 0 });
    const [planSales, setPlanSales] = useState([]);
    const [userStatus, setUserStatus] = useState({ byStatus: [], byBranch: [], byUserType: [] });
    const [groupBy, setGroupBy] = useState('day');
    // The date and time the reports cover (none: the server's last 30 days).
    const [range, setRange] = useState({ from: '', to: '' });

    const params = useCallback(() => {
        const p = { groupBy };
        if (selectedBranch) p.branchId = selectedBranch._id;
        const { from, to } = rangeParams(range);
        if (from) p.from = from;
        if (to) p.to = to;
        return p;
    }, [selectedBranch, groupBy, range]);

    const fetchAll = useCallback(async () => {
        try {
            setLoading(true);
            const p = params();
            const [usageRes, revRes, salesRes, statusRes] = await Promise.all([
                reportAPI.getUsage(p), reportAPI.getRevenue(p), reportAPI.getPlanSales(p), reportAPI.getUserStatus(p),
            ]);
            setUsage(usageRes.data.data || []);
            setRevenue(revRes.data.data || { byDay: [], total: 0 });
            setPlanSales(salesRes.data.data || []);
            setUserStatus(statusRes.data.data || { byStatus: [], byBranch: [], byUserType: [] });
        } catch (error) {
            toast.error('Failed to fetch reports');
        } finally {
            setLoading(false);
        }
    }, [params]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    // The report's table from the server (its columns, format=json), as an Excel file.
    const handleExport = async (type, label) => {
        try {
            const res = await reportAPI.exportTable(type, params());
            exportTableToExcel(`${type}-report-${new Date().toISOString().slice(0, 10)}`, res.data, { sheetName: label });
            toast.success(`${label} exported`);
        } catch (error) {
            toast.error('Export failed');
        }
    };

    const formatBytes = (b) => {
        if (!b) return '0 MB';
        if (b >= 1073741824) return `${(b / 1073741824).toFixed(1)} GB`;
        if (b >= 1048576) return `${(b / 1048576).toFixed(1)} MB`;
        return `${(b / 1024).toFixed(1)} KB`;
    };

    return (
        <div className="page-container-modern">
            <div className="page-header-modern">
                <div className="header-content">
                    <div className="header-text">
                        <h1 className="page-title-modern"><i className="fas fa-chart-bar"></i> Reports</h1>
                        <p className="page-subtitle-modern">Usage, revenue, plan sales, and user status — exportable to Excel</p>
                    </div>
                    <div className="header-actions">
                        <select value={groupBy} onChange={e => setGroupBy(e.target.value)} className="reports-groupby">
                            <option value="day">Daily</option>
                            <option value="month">Monthly</option>
                        </select>
                    </div>
                </div>
            </div>

            <div className="reports-when">
                <DateTimeRange value={range} onChange={setRange} />
                {!range.from && !range.to && <span className="reports-when-note">Showing the last 30 days</span>}
            </div>

            {loading ? (
                <div className="loading-modern"><div className="spinner-modern"></div><p>Loading reports...</p></div>
            ) : (
                <div className="reports-grid">
                    <ReportCard icon="fa-chart-area" title="Usage Report" onExport={() => handleExport('usage', 'Usage report')}>
                        <div className="reports-table-wrap">
                            <table className="table-modern">
                                <thead><tr><th className="reports-sr">Sr. No.</th><th>Period</th><th>Sessions</th><th>Data Used</th></tr></thead>
                                <tbody>
                                    {usage.map((u, i) => (
                                        <tr key={i}><td className="reports-sr">{i + 1}</td><td>{u.period}</td><td>{u.sessions}</td><td>{formatBytes(u.totalBytes)}</td></tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </ReportCard>

                    <ReportCard icon="fa-rupee-sign" title={`Revenue Report — ₹${revenue.total.toLocaleString('en-IN')}`} onExport={() => handleExport('revenue', 'Revenue report')}>
                        <div className="reports-table-wrap">
                            <table className="table-modern">
                                <thead><tr><th className="reports-sr">Sr. No.</th><th>Date</th><th>Revenue</th><th>Transactions</th></tr></thead>
                                <tbody>
                                    {revenue.byDay.map((r, i) => (
                                        <tr key={i}><td className="reports-sr">{i + 1}</td><td>{r.date}</td><td>₹{r.revenue.toLocaleString('en-IN')}</td><td>{r.transactions}</td></tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </ReportCard>

                    <ReportCard icon="fa-tags" title="Plan Sales" onExport={() => handleExport('plan-sales', 'Plan sales report')}>
                        <div className="reports-table-wrap">
                            <table className="table-modern">
                                <thead><tr><th className="reports-sr">Sr. No.</th><th>Plan</th><th>Users</th><th>Price</th></tr></thead>
                                <tbody>
                                    {planSales.map((p, i) => (
                                        <tr key={i}><td className="reports-sr">{i + 1}</td><td>{p.planName}</td><td>{p.userCount}</td><td>₹{p.price}</td></tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </ReportCard>

                    <ReportCard icon="fa-users" title="User Status" onExport={() => handleExport('users', 'Users report')}>
                        <div className="reports-status-grid">
                            <div>
                                <h4>By Status</h4>
                                {userStatus.byStatus.map((s, i) => (
                                    <div key={i} className="reports-status-row"><span>{s.status}</span><strong>{s.count}</strong></div>
                                ))}
                            </div>
                            <div>
                                <h4>By User Type</h4>
                                {userStatus.byUserType.map((u, i) => (
                                    <div key={i} className="reports-status-row"><span>{u.userType}</span><strong>{u.count}</strong></div>
                                ))}
                            </div>
                        </div>
                    </ReportCard>

                    <ReportCard icon="fa-history" title="Plan History" onExport={() => handleExport('plan-history', 'Plan history')}>
                        <p className="reports-hint">Full upgrade/downgrade audit trail — export to Excel for detailed review.</p>
                    </ReportCard>
                </div>
            )}
        </div>
    );
};

export default Reports;
